import React, { useState } from 'react'
import injectStyles from 'react-jss'
import { useDispatch } from 'react-redux'

import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'

import ExpandCollapseBox from 'Components/ExpandCollapseBox'
import CountrySettingsTable from './Components/CountrySettingsTable'

import { showReduxModal } from '../../../../../../Reducers/UI/actions'
import { updateCountriesDefaultSettings } from '../../../../gql'

import {
  BA_PER_LOCATION,
  CAMPAIGN_COSTS,
  CAMPAIGN_LIMITS,
  DEFAULT_CAMPAIGN_LIMITS,
  TAX_RATE,
} from '../../../../constants'

import styles from './styles.js'
import ChannelSettingsTable from './Components/ChannelSettingsTable'

const CountrySettings = ({
  classes,
  countryId,
  refetch,
  printingCosts = [],
  ...props
}) => {
  const [countrySettings, setCountrySettings] = useState(
    props.countryDefaultSettings,
  )
  const dispatch = useDispatch()
  const getError = () => {
    let valid = true
    const nonLimits = [...CAMPAIGN_LIMITS.slice(0, 2)].reduce((acc, curr) => {
      const key = curr.key
      const currValue = countrySettings[key]

      if (!currValue) {
        // if (key !== 'weeklyMissionLimit' && currValue !== null) {
        if (currValue !== null) {
          acc.push(key)
        }
      }
      return acc
    }, [])

    if (
      (countrySettings.enabledChannelLetterbox && nonLimits.length) ||
      (countrySettings.enabledChannelLetterbox &&
        !countrySettings.flyerPackD2d) ||
      (countrySettings.enabledChannelLetterbox &&
        !countrySettings.d2dDistributionCosts)
    ) {
      valid = false
    }

    if (
      ((countrySettings.enabledChannelPreciseLocations ||
        countrySettings.enabledChannelBroadAreas) &&
        nonLimits.length) ||
      ((countrySettings.enabledChannelPreciseLocations ||
        countrySettings.enabledChannelBroadAreas) &&
        !countrySettings.flyerPackH2h) ||
      ((countrySettings.enabledChannelPreciseLocations ||
        countrySettings.enabledChannelBroadAreas) &&
        !countrySettings.h2hDistributionCosts)
    ) {
      valid = false
    }

    if (!valid) {
      return 'Mission related values cannot be set to 0 while a channel is active. Please deactivate the channel first.'
    } else {
      if (!countrySettings.baPayout) {
        return 'The BA payout per mission value should always be greater than 0'
      }
      return null
    }
  }

  const handleSaveCountryDefaultSettings = async () => {
    const error = getError()
    if (error) {
      dispatch(
        showReduxModal({
          title: 'Cannot save',
          onConfirm: () => {},
          children: <div style={{ textAlign: 'center' }}>{error}</div>,
          confirmLabel: 'OK',
          hideCancel: true,
        }),
      )
    } else {
      const floatKeys = [...CAMPAIGN_COSTS, ...TAX_RATE].map(
        option => option.key,
      )
      const mappedData = Object.entries({
        ...countrySettings,
        ...(countryId == 14
          ? { enabledChannelEddm: true }
          : { enabledChannelEddm: false }),
      }).reduce((acc, [key, value]) => {
        let formattedValue

        // A country that has never had its settings saved comes back with
        // nulls (and a half-typed "12." is still a string): calling toFixed on
        // either threw, and the click did nothing at all.
        if (value === null || typeof value === 'boolean') {
          formattedValue = value
        } else if (floatKeys.includes(key)) {
          formattedValue = Number(Number(value).toFixed(2))
        } else {
          formattedValue = Number(value)
        }

        return {
          ...acc,
          [key]: formattedValue,
        }
      }, {})
      const response = await updateCountriesDefaultSettings({
        ...mappedData,
        id: countryId,
      })
      const {
        data: { updateCountriesDefaultSettings: status },
      } = await response.json()

      refetch()

      return status
    }
  }

  return (
    <ExpandCollapseBox title={'Country Default Settings'}>
      <Box className={classes.subTitle}>
        <Typography>
          By default, these values will be applied to all campaigns in the
          self-serve in this country.
        </Typography>
        <Typography>
          If you need unique values for any specific city or cities, please
          define these in ‘City Settings’ table further down.
        </Typography>
        <Typography>
          Any costs that are 0 will be excluded from self-serve campaigns.
        </Typography>
      </Box>
      <Box className={classes.tables}>
        <CountrySettingsTable
          title={'Default campaign costs'}
          countrySettings={countrySettings}
          setCountrySettings={setCountrySettings}
          options={CAMPAIGN_COSTS}
          onSave={handleSaveCountryDefaultSettings}
          {...props}
        />
        <Box>
          <CountrySettingsTable
            title={'Default campaign limits'}
            countrySettings={countrySettings}
            setCountrySettings={setCountrySettings}
            options={CAMPAIGN_LIMITS}
            onSave={handleSaveCountryDefaultSettings}
            {...props}
          />
          <CountrySettingsTable
            title={'Tax rate'}
            countrySettings={countrySettings}
            setCountrySettings={setCountrySettings}
            options={TAX_RATE}
            onSave={handleSaveCountryDefaultSettings}
            {...props}
          />
          <CountrySettingsTable
            title={'BAs per H2H location'}
            countrySettings={countrySettings}
            setCountrySettings={setCountrySettings}
            options={BA_PER_LOCATION}
            onSave={handleSaveCountryDefaultSettings}
            {...props}
          />
          <CountrySettingsTable
            title={'Earliest selectable day (client)'}
            countrySettings={countrySettings}
            setCountrySettings={setCountrySettings}
            options={DEFAULT_CAMPAIGN_LIMITS}
            onSave={handleSaveCountryDefaultSettings}
            {...props}
          />
        </Box>
      </Box>
      <Box className={classes.tables}>
        <ChannelSettingsTable
          title={'Self-serve channels'}
          countrySettings={countrySettings}
          defaultCountrySettings={props.countryDefaultSettings}
          setCountrySettings={setCountrySettings}
          onSave={handleSaveCountryDefaultSettings}
          {...props}
        />
      </Box>
      <Box className={classes.description}>
        <Typography>
          If LIVE, the channel can still be disabled in specific cities by using
          the city settings table below.
        </Typography>
        <Typography>
          If DISABLED, the channel will be disabled in the entire country,
          including <strong>all</strong> cities in the country.
        </Typography>
      </Box>
    </ExpandCollapseBox>
  )
}

export default injectStyles(styles)(CountrySettings)
